/**
 * WORLD street block — wet asphalt roadway, sidewalks with curb cuts at the
 * alley mouth, brick facades both sides, alley corridor to the office door,
 * end caps, and the bar door under the neon sign.
 *
 * Wet look: roughness/envmap trick (CONTRACTS fallback #2) — planar reflection
 * was skipped because WORLD has no renderer handle and a Reflector pass fights
 * the noir fog; low-roughness puddle map + procedural night cube map instead.
 */
import { BoxGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three'
import { STREET_Z_MAX, STREET_Z_MIN } from '../core/constants'
import {
  ALLEY_HALF_WIDTH,
  ALLEY_WALL_HEIGHT,
  ALLEY_X_END,
  ALLEY_X_START,
  BAR_DOOR_HEIGHT,
  BAR_DOOR_WIDTH,
  BAR_DOOR_Z,
  COLOR_DOOR_OXBLOOD,
  END_WALL_HEIGHT,
  END_WALL_SPAN,
  FACADE_CENTER_X,
  FACADE_DEPTH,
  FACADE_FRONT_X,
  FACADE_MIN_HEIGHT,
  LIT_WINDOW_DEFS,
  LIT_WINDOW_FRAME_COLOR,
  LIT_WINDOW_MULLION,
  LIT_WINDOW_PROUD,
  ROAD_LENGTH,
  ROAD_WIDTH,
  SIDEWALK_CENTER_X,
  SIDEWALK_HEIGHT,
  SIDEWALK_WIDTH
} from './constants'
import { enableShadows, interactRadius, tagBloom, tagInteract, tagSolid } from './helpers'
import {
  createAsphaltMaps,
  createBrickTexture,
  createConcreteTexture,
  createNightEnvMap
} from './textures'

const ASPHALT_ROUGHNESS = 0.12
const ASPHALT_METALNESS = 0.55
const ASPHALT_ENV_INTENSITY = 2.2
/** Rain-slick concrete — low enough that lamp light streaks across it. */
const CONCRETE_ROUGHNESS = 0.45
const CONCRETE_METALNESS = 0.18
const BRICK_ROUGHNESS = 0.92

/** Deterministic-ish building strip heights, west then east. */
const WEST_SEGMENTS: ReadonlyArray<readonly [number, number, number]> = [
  // [zStart, zEnd, height]
  [STREET_Z_MIN, -26, 12],
  [-26, -12, 9.5],
  [-12, 4, 14],
  [4, 18, 10],
  [18, STREET_Z_MAX, 12.5]
]

const EAST_SEGMENTS: ReadonlyArray<readonly [number, number, number]> = [
  // Gap at z in [-ALLEY_HALF_WIDTH, ALLEY_HALF_WIDTH] for the alley mouth.
  [STREET_Z_MIN, -18, 11],
  [-18, -6, 10], // the bar building (door + neon live here)
  [-6, -ALLEY_HALF_WIDTH, 13],
  [ALLEY_HALF_WIDTH, 14, FACADE_MIN_HEIGHT],
  [14, STREET_Z_MAX, 12]
]

const createRoad = (): Mesh => {
  const { map, roughnessMap } = createAsphaltMaps()
  map.repeat.set(1, 8)
  roughnessMap.repeat.set(1, 8)
  const material = new MeshStandardMaterial({
    color: 0xffffff,
    map,
    roughnessMap,
    roughness: ASPHALT_ROUGHNESS,
    metalness: ASPHALT_METALNESS,
    envMap: createNightEnvMap(),
    envMapIntensity: ASPHALT_ENV_INTENSITY
  })
  const road = new Mesh(new PlaneGeometry(ROAD_WIDTH, ROAD_LENGTH), material)
  road.rotation.x = -Math.PI / 2
  road.receiveShadow = true
  tagSolid(road)
  return road
}

const createSidewalkSlab = (
  material: MeshStandardMaterial,
  centerX: number,
  zStart: number,
  zEnd: number
): Mesh => {
  const length = zEnd - zStart
  const slab = new Mesh(
    new BoxGeometry(SIDEWALK_WIDTH, SIDEWALK_HEIGHT, length),
    material
  )
  slab.position.set(centerX, SIDEWALK_HEIGHT / 2, zStart + length / 2)
  slab.receiveShadow = true
  tagSolid(slab)
  return slab
}

const createSidewalks = (): Group => {
  const group = new Group()
  group.name = 'sidewalks'
  const texture = createConcreteTexture()
  texture.repeat.set(2, 40)
  const material = new MeshStandardMaterial({
    map: texture,
    roughness: CONCRETE_ROUGHNESS,
    metalness: CONCRETE_METALNESS,
    envMap: createNightEnvMap(),
    envMapIntensity: 0.6
  })
  // West: continuous. East: split around the alley mouth (curb cut at road level).
  group.add(createSidewalkSlab(material, -SIDEWALK_CENTER_X, STREET_Z_MIN, STREET_Z_MAX))
  group.add(createSidewalkSlab(material, SIDEWALK_CENTER_X, STREET_Z_MIN, -ALLEY_HALF_WIDTH))
  group.add(createSidewalkSlab(material, SIDEWALK_CENTER_X, ALLEY_HALF_WIDTH, STREET_Z_MAX))
  return group
}

const createFacadeRow = (
  segments: ReadonlyArray<readonly [number, number, number]>,
  centerX: number,
  hue: number
): Group => {
  const group = new Group()
  group.name = centerX < 0 ? 'facades-west' : 'facades-east'
  segments.forEach(([zStart, zEnd, height]) => {
    const texture = createBrickTexture(hue + (Math.random() - 0.5) * 8)
    const length = zEnd - zStart
    texture.repeat.set(length / 2.5, height / 2.5)
    const material = new MeshStandardMaterial({
      map: texture,
      roughness: BRICK_ROUGHNESS,
      metalness: 0
    })
    const building = new Mesh(new BoxGeometry(FACADE_DEPTH, height, length), material)
    building.position.set(centerX, height / 2, zStart + length / 2)
    enableShadows(building)
    tagSolid(building)
    group.add(building)
  })
  return group
}

const createEndCaps = (): Group => {
  const group = new Group()
  group.name = 'end-caps'
  const texture = createBrickTexture(8)
  texture.repeat.set(10, 5)
  const material = new MeshStandardMaterial({ map: texture, roughness: BRICK_ROUGHNESS })
  const ends: readonly number[] = [STREET_Z_MIN - FACADE_DEPTH / 2, STREET_Z_MAX + FACADE_DEPTH / 2]
  ends.forEach((z) => {
    const wall = new Mesh(
      new BoxGeometry(END_WALL_SPAN, END_WALL_HEIGHT, FACADE_DEPTH),
      material
    )
    wall.position.set(0, END_WALL_HEIGHT / 2, z)
    enableShadows(wall)
    tagSolid(wall)
    group.add(wall)
  })
  return group
}

const createAlley = (): Group => {
  const group = new Group()
  group.name = 'alley'
  const length = ALLEY_X_END - ALLEY_X_START
  const floorTexture = createConcreteTexture()
  floorTexture.repeat.set(length / 2, ALLEY_HALF_WIDTH)
  const floor = new Mesh(
    new BoxGeometry(length, 0.05, ALLEY_HALF_WIDTH * 2),
    new MeshStandardMaterial({ map: floorTexture, roughness: 0.7, metalness: 0.15 })
  )
  floor.position.set(ALLEY_X_START + length / 2, 0.025, 0)
  floor.receiveShadow = true
  tagSolid(floor)
  group.add(floor)

  const wallTexture = createBrickTexture(14)
  wallTexture.repeat.set(length / 2.5, ALLEY_WALL_HEIGHT / 2.5)
  const wallMaterial = new MeshStandardMaterial({ map: wallTexture, roughness: BRICK_ROUGHNESS })
  const sides: readonly number[] = [-1, 1]
  sides.forEach((side) => {
    const wall = new Mesh(new BoxGeometry(length, ALLEY_WALL_HEIGHT, 0.3), wallMaterial)
    wall.position.set(
      ALLEY_X_START + length / 2,
      ALLEY_WALL_HEIGHT / 2,
      side * (ALLEY_HALF_WIDTH + 0.15)
    )
    enableShadows(wall)
    tagSolid(wall)
    group.add(wall)
  })
  return group
}

/**
 * Lit upper-story windows staggered in Z — depth practicals that lead the
 * eye down the street. LIGHT adds matching dim point lights at the same
 * coordinates so the surrounding brick picks up the glow.
 */
const createLitWindows = (): Group => {
  const group = new Group()
  group.name = 'lit-windows'
  const frameMaterial = new MeshStandardMaterial({
    color: LIT_WINDOW_FRAME_COLOR,
    roughness: 0.8,
    metalness: 0.1
  })
  LIT_WINDOW_DEFS.forEach((def) => {
    const facing = def.position.x > 0 ? -1 : 1
    const paneX = def.position.x + facing * LIT_WINDOW_PROUD
    const pane = new Mesh(
      new PlaneGeometry(def.width, def.height),
      new MeshStandardMaterial({
        color: 0x000000,
        emissive: def.color,
        emissiveIntensity: def.emissiveIntensity,
        roughness: 0.3,
        metalness: 0
      })
    )
    pane.rotation.y = facing * (Math.PI / 2)
    pane.position.set(paneX, def.position.y, def.position.z)
    tagBloom(pane)
    group.add(pane)

    // Mullion cross so the glow reads as a window, not a rectangle of light.
    const mullionX = def.position.x + facing * (LIT_WINDOW_PROUD + 0.01)
    const vertical = new Mesh(
      new BoxGeometry(0.02, def.height, LIT_WINDOW_MULLION),
      frameMaterial
    )
    vertical.position.set(mullionX, def.position.y, def.position.z)
    group.add(vertical)
    const horizontal = new Mesh(
      new BoxGeometry(0.02, LIT_WINDOW_MULLION, def.width),
      frameMaterial
    )
    horizontal.position.set(mullionX, def.position.y, def.position.z)
    group.add(horizontal)
  })
  return group
}

const createBarDoor = (): Mesh => {
  const door = new Mesh(
    new BoxGeometry(0.15, BAR_DOOR_HEIGHT, BAR_DOOR_WIDTH),
    new MeshStandardMaterial({ color: COLOR_DOOR_OXBLOOD, roughness: 0.6, metalness: 0.1 })
  )
  door.position.set(FACADE_FRONT_X - 0.05, SIDEWALK_HEIGHT + BAR_DOOR_HEIGHT / 2, BAR_DOOR_Z)
  enableShadows(door)
  tagSolid(door)
  tagInteract(door, {
    id: 'door-bar',
    kind: 'door',
    prompt: 'The Blue Dahlia — locked tight',
    radius: interactRadius()
  })
  return door
}

export const buildStreet = (): Group => {
  const street = new Group()
  street.name = 'street'
  street.add(createRoad())
  street.add(createSidewalks())
  street.add(createFacadeRow(WEST_SEGMENTS, -FACADE_CENTER_X, 10))
  street.add(createFacadeRow(EAST_SEGMENTS, FACADE_CENTER_X, 16))
  street.add(createEndCaps())
  street.add(createAlley())
  street.add(createLitWindows())
  street.add(createBarDoor())
  return street
}
